import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Sun, Droplets, Flame, Footprints, Waves, Truck, ShoppingCart, Fish, Ship, Mountain, Gem, Palette } from "lucide-react";
import EditableText from "@/components/editor/EditableText";

const ON_SITE = [
  { icon: Sun, label: "Bring a generator — no power hookup" },
  { icon: Droplets, label: "Self-contained RVs and campers required" },
  { icon: Flame, label: "Fire pit area" },
  { icon: Footprints, label: "Picnic / outdoor gathering area (planned)" },
  { icon: Footprints, label: "Hiking trails to the shore" },
  { icon: Waves, label: "Private waterfront access" },
];

const NEARBY = [
  { icon: Truck, label: "Pump-out station (short drive)" },
  { icon: ShoppingCart, label: "Digby town — groceries, restaurants, fuel" },
  { icon: Ship, label: "Whale watching tours from Digby" },
  { icon: Fish, label: "Brier Island — world-class whale watching (ferry)" },
  { icon: Mountain, label: "Balancing Rock trail" },
  { icon: Ship, label: "Long Island & Tiverton" },
  { icon: Gem, label: "Fresh scallops — Digby is the scallop capital of the world" },
  { icon: Palette, label: "Bear River — charming arts village" },
];

function ServiceItem({ icon: Icon, label }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-wc-teal mt-0.5 flex-shrink-0" />
      <span className="font-body text-wc-slate text-sm leading-relaxed">{label}</span>
    </div>
  );
}

export default function Services() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="services" data-testid="services-section" className="wc-section bg-wc-cream" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p className="font-accent italic text-wc-teal text-base mb-3">Services</p>
          <EditableText contentKey="services_heading" as="h2"
            className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4"
            defaultValue="What's here & nearby." />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16"
        >
          <div>
            <h3 className="font-display text-wc-forest text-xl font-semibold mb-4 pb-3 border-b border-wc-driftwood/40">
              On-Site
            </h3>
            <div className="space-y-1">
              {ON_SITE.map((item, i) => (
                <ServiceItem key={i} {...item} />
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-display text-wc-forest text-xl font-semibold mb-4 pb-3 border-b border-wc-driftwood/40">
              Nearby
            </h3>
            <div className="space-y-1">
              {NEARBY.map((item, i) => (
                <ServiceItem key={i} {...item} />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
